import React from "react";
import { cn } from "@/lib/utils";
import { LogoIcon } from "@/components/logo";

interface LoadingStateProps {
  variant?: "full" | "section" | "inline";
  message?: string;
  className?: string;
  showLogo?: boolean;
}

/**
 * Componente de carga optimizado y ligero.
 * Exportado como función tradicional para asegurar compatibilidad con HMR.
 */
export function LoadingState({
  variant = "section",
  message = "Cargando...",
  className = "",
  showLogo = true,
}: LoadingStateProps) {
  const isFull = variant === "full";
  const isInline = variant === "inline";

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "flex flex-col items-center justify-center animate-in fade-in duration-500",
        isFull && "fixed inset-0 z-50 bg-background/60 backdrop-blur-md",
        variant === "section" && "min-h-[200px] w-full py-10",
        isInline && "flex-row gap-2.5",
        className,
      )}
    >
      <div className="flex flex-col items-center gap-3">
        <div className="relative flex items-center justify-center">
          {/* Spinner Minimalista */}
          <div
            className={cn(
              "relative flex items-center justify-center shrink-0",
              isInline ? "h-4 w-4" : "h-10 w-10",
            )}
          >
            <div
              className={cn(
                "absolute inset-0 rounded-full border-b-2 animate-spin",
                isInline ? "border-current" : "border-primary",
              )}
            />
            <div
              className={cn(
                "absolute inset-0 rounded-full border-2",
                isInline ? "opacity-20 border-white" : "border-primary/5",
              )}
            />
          </div>

          {showLogo && !isInline && (
            <div className="absolute inset-0 flex items-center justify-center">
              <LogoIcon className="h-4 w-4 opacity-40 animate-pulse" />
            </div>
          )}
        </div>

        {message && !isInline && (
          <span className="font-bold tracking-tight uppercase text-xs text-muted-foreground">
            {message}
          </span>
        )}
      </div>

      {message && isInline && (
        <span className="font-bold tracking-tight uppercase text-xs md:text-sm text-inherit">
          {message}
        </span>
      )}
    </div>
  );
}
